import axios, { AxiosError } from 'axios';
import { auth } from '../firebase';
import type { ProductVariant } from '../types/commerce';
import { backendAPI } from './products';

interface ApiError {
  message?: string;
  error?: string;
  code?: string;
}

export type VariantPayload = {
  sku?: string | null;
  title: string;
  price: number;
  quantity: number;
  compareAtPrice?: number | null;
  cost?: number | null;
  barcode?: string | null;
  weight?: number | null;
  attributes?: Record<string, unknown> | null;
  isDefault?: boolean;
  active?: boolean;
};

export type VariantUpdatePayload = Partial<VariantPayload>;

async function authHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not logged in');
  const idToken = await user.getIdToken();
  return { Authorization: `Bearer ${idToken}` };
}

function mapVariantError(error: unknown, fallback: string): Error {
  const axiosError = error as AxiosError<ApiError>;
  const message =
    axiosError?.response?.data?.error ||
    axiosError?.response?.data?.message ||
    fallback;
  return new Error(message);
}

export const list_product_variants = async (
  productId: number
): Promise<ProductVariant[]> => {
  try {
    const response = await backendAPI.get(
      `/products/${productId}/variants`
    );
    return response.data?.variants ?? [];
  } catch (error) {
    throw mapVariantError(error, 'Failed to fetch variants');
  }
};

export const get_product_variant = async (
  variantId: number
): Promise<ProductVariant> => {
  try {
    const response = await backendAPI.get(
      `/products/variants/${variantId}`
    );
    return response.data?.variant;
  } catch (error) {
    throw mapVariantError(error, 'Failed to fetch variant');
  }
};

export const create_product_variant = async (
  productId: number,
  payload: VariantPayload
): Promise<ProductVariant> => {
  try {
    const headers = await authHeaders();
    const response = await backendAPI.post(
      `/products/${productId}/variants`,
      payload,
      { headers }
    );
    return response.data?.variant;
  } catch (error) {
    throw mapVariantError(error, 'Failed to create variant');
  }
};

export const update_product_variant = async (
  variantId: number,
  payload: VariantUpdatePayload
): Promise<ProductVariant> => {
  try {
    const headers = await authHeaders();
    const response = await backendAPI.patch(
      `/products/variants/${variantId}`,
      payload,
      { headers }
    );
    return response.data?.variant;
  } catch (error) {
    throw mapVariantError(error, 'Failed to update variant');
  }
};

export const delete_product_variant = async (
  variantId: number
): Promise<void> => {
  try {
    const headers = await authHeaders();
    await backendAPI.delete(`/products/variants/${variantId}`, { headers });
  } catch (error) {
    throw mapVariantError(error, 'Failed to delete variant');
  }
};
